import { useModal } from 'react-modal-hook';
import { useTranslation } from 'react-i18next';

import { GetOrderQuery as GetOrder } from '__generated__/graphql';
import { useNotifier } from '../../../../common/Notifications/NotificationProvider';
import SendOrderConfirmationMutation from './SendOrderConfirmationMutation';
import UI from '../../../../common/UI';
import useLocale from '../../../../common/useLocale';
import useMutation from '../../../../api/useMutation';

type Order = GetOrder['order'];

export interface SendConfirmationFormProps {
  order: Order;
  onSuccess?: () => void;
  onCancel?: () => void;
}

export const SendConfirmationForm = ({ order, onSuccess, onCancel }: SendConfirmationFormProps) => {
  const { t } = useTranslation();
  const { formatCurrency } = useLocale();
  const notifier = useNotifier();

  const [sendConfirmation, { loading: sending }] = useMutation(
    SendOrderConfirmationMutation,
    {
      onCompleted: () => {
        // eslint-disable-next-line @typescript-eslint/no-use-before-define
        onSuccess?.();
        notifier.success(t('notifications.confirmation_sent'));
      },
    },
  );

  return (
    <UI.Form onSubmit={() => sendConfirmation({ variables: { id: order.id } })}>
      <UI.FormGrid>
        <UI.H4>
          {t('send_confirmation')}
        </UI.H4>
        <UI.HR />
        <UI.InputGroup>
          <UI.InputLabel>{t('to')}</UI.InputLabel>
          {order.participant.email}
        </UI.InputGroup>
        <UI.InputGroup>
          <UI.InputLabel>{t('order')}</UI.InputLabel>
          {order.invoice_id}
          {' ('}
          {formatCurrency(order.amount)}
          )
        </UI.InputGroup>
        <UI.HR />
        <UI.Div sc={{ textAlign: 'right' }}>
          {onCancel && (
            <UI.Button onClick={() => onCancel()} disabled={sending}>
              {t('cancel')}
            </UI.Button>
          )}
          {' '}
          <UI.Button
            type="submit"
            sc={{ brand: 'secondary' }}
            disabled={sending}
          >
            {t('confirm')}
          </UI.Button>
        </UI.Div>
      </UI.FormGrid>
    </UI.Form>
  );
};
interface UseSendConfirmationModalProps {
  order?: Order;
}

const useSendConfirmationModal = ({ order }: UseSendConfirmationModalProps) => {
  const [openModal, closeModal] = useModal(
    () => (
      <UI.Modal close={closeModal}>
        {!order && <UI.PageLoader />}
        {order && <SendConfirmationForm order={order} onSuccess={closeModal} onCancel={closeModal} />}
      </UI.Modal>
    ),
    [order],
  );

  return [openModal, closeModal];
};

export default useSendConfirmationModal;
